'use client';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, Org } from './types';

interface AuthState {
  user: User | null;
  org: Org | null;
  token: string | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
}

const initialState: AuthState = {
  user: null,
  org: null,
  token: null,
  isAuthenticated: false,
  isHydrated: false,
};

// Helper to safely access localStorage
const getStoredToken = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('token');
  }
  return null;
};

const getStoredUser = (): User | null => {
  if (typeof window !== 'undefined') {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
  return null;
};

const getStoredOrg = (): Org | null => {
  if (typeof window !== 'undefined') {
    const org = localStorage.getItem('org');
    return org ? JSON.parse(org) : null;
  }
  return null;
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ user: User; org: Org; token: string }>
    ) => {
      state.user = action.payload.user;
      state.org = action.payload.org;
      state.token = action.payload.token;
      state.isAuthenticated = true;

      // Persist to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('token', action.payload.token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
        localStorage.setItem('org', JSON.stringify(action.payload.org));
      }
    },
    logout: (state) => {
      state.user = null;
      state.org = null;
      state.token = null;
      state.isAuthenticated = false;

      // Clear localStorage
      if (typeof window !== 'undefined') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('org');
      }
    },
    hydrateAuth: (state) => {
      const token = getStoredToken();
      const user = getStoredUser();
      const org = getStoredOrg();

      if (token && user && org) {
        state.token = token;
        state.user = user;
        state.org = org;
        state.isAuthenticated = true;
      }
      state.isHydrated = true;
    },
  },
});

export const { setCredentials, logout, hydrateAuth } = authSlice.actions;
export default authSlice.reducer;
